import styled, { css } from 'styled-components';
import { breakpoint, sizes } from 'variables';
import type { ProductInformationViewModel, SplashModel } from 'autogen/swagger/all';
import GalleryMediaPreviewer from 'components/GalleryMediaPreviewer';
import type { EmaLogoViewModel } from 'autogen/swagger/all';
import useUnavailableVideos from 'components/video/hooks/useUnavailableVideos';
import {
    type GalleryMediaItem,
    type GalleryMediaItems,
    MediaItemDiscriminator
} from 'components/GalleryMediaPreviewer/shared/types';
import SplashOverlay from 'components/product/ProductItemGallery/components/SplashOverlay';
import UAEventTracking from 'modules/tracking/UAEventTracking';
import { useIsChanel } from 'hooks/user/useIsBrand';
import type { ScrollType } from 'components/emblaCarousel';
import {
    mapAutoplayVideosToMediaItems,
    mapProductInformationModelToGalleryMediaItems,
    prependHighlightedVideoMediaItemToMediaItems
} from './shared/helpers';
import { GA4Tracking } from 'modules/tracking/GA4';
import { useRegimeStore } from '../regime/shared/regimeStore';
import RegimeQuickViewButton from '../regime/components/RegimeQuickView/components/RegimeQuickViewButton';
import WhenSplitTestVariant from 'helpers/WhenSplitTestVariant';

const StyledGalleryMediaPreviewer = styled(GalleryMediaPreviewer)<{ $isChanel: boolean }>`
    width: 100%;
    height: 314px;
    position: relative;

    ${breakpoint.up(sizes.lg)} {
        height: 596px; // 596px = 120*4 + 12*3 + 40*2 = Thumbnail height * 4 + gap * 3 + container padding-x * 2

        ${({ $isChanel }) =>
            $isChanel &&
            css`
                height: 344px; // 344px = 80*4 + 8*3 = Thumbnail height * 4 + gap * 3
            `}
    }
`;
const pipe =
    (...fns) =>
    (x) =>
        fns.reduce((v, f) => f(v), x);

const removeUnavailableVideos = (mediaItems, unavailableVideoIds) =>
    mediaItems.filter((mediaItem) =>
        MediaItemDiscriminator(
            mediaItem,
            () => true,
            (videoItem) => !unavailableVideoIds.has(videoItem.video.id),
            (highlightedVideoItem) => !unavailableVideoIds.has(highlightedVideoItem.video.id),
            () => true,
            () => false
        )
    );

let disableZoomTracking = false;
function onOpen(pswpInstance) {
    pswpInstance.on('change', () => {
        const isVideo = pswpInstance?.currSlide?.data.type === 'html';
        UAEventTracking({
            eventCategory: 'productPage',
            eventAction: 'change',
            eventLabel: isVideo ? 'Lightbox changed video' : 'Lightbox changed image'
        });
        GA4Tracking({
            eventName: 'product_gallery',
            category: 'navigation',
            context: 'product_page',
            eventParams: [{ param_name: 'slide_number', param_value: pswpInstance?.currIndex + 1 }]
        });
    });
    pswpInstance.on('zoomPanUpdate', () => {
        if (!disableZoomTracking) {
            disableZoomTracking = true;

            UAEventTracking({
                eventCategory: 'productPage',
                eventAction: 'zoom',
                eventLabel: 'Lightbox zoomed'
            });
        }
    });

    GA4Tracking({
        eventName: 'product_gallery',
        category: 'navigation',
        context: 'product_page',
        eventParams: [{ param_name: 'toggle', param_value: true }]
    });
}

function onThumbnailClick({ index, type }: { index: number; type: GalleryMediaItem['type'] }) {
    UAEventTracking({
        eventCategory: 'productPage',
        eventAction: 'click',
        eventLabel: `Thumbnail - position: ${index + 1} - ${type}`
    });
    GA4Tracking({
        context: 'product_page',
        category: 'ecommerce',
        eventName: 'click',
        eventParams: [
            {
                param_name: 'slide_number',
                param_value: index + 1
            },
            { param_name: 'modal_type', param_value: 'light_box' },
            { param_name: 'thumbnail_type', param_value: type }
        ]
    });
}

function onPaginate({ index, type }: { index: number; type?: GalleryMediaItem['type'] }) {
    UAEventTracking({
        eventCategory: 'productPage',
        eventAction: 'click',
        eventLabel: `Pagination - position ${index + 1} - ${type}`
    });
}

function onCarouselScrollClick(scrollType: ScrollType) {
    UAEventTracking({
        eventCategory: 'productPage',
        eventAction: 'click',
        eventLabel: `Gallery slider button - ${scrollType}`
    });
}

export default function ProductItemGallery({
    items,
    shouldAutoplay = false,
    splashes,
    emaLogo
}: {
    items: ProductInformationViewModel['productMedia'];
    shouldAutoplay?: boolean;
    splashes?: SplashModel;
    emaLogo?: EmaLogoViewModel;
}) {
    const isChanel = useIsChanel();
    const hasOneItem = items.length === 1;
    const mediaItems: GalleryMediaItems = shouldAutoplay
        ? pipe(
              mapProductInformationModelToGalleryMediaItems,
              mapAutoplayVideosToMediaItems,
              prependHighlightedVideoMediaItemToMediaItems
          )(items)
        : pipe(mapProductInformationModelToGalleryMediaItems, prependHighlightedVideoMediaItemToMediaItems)(items);

    const { unavailableVideoIds } = useUnavailableVideos();
    const filteredMediaItems = removeUnavailableVideos(mediaItems, unavailableVideoIds); // if a video fails to load/play, remove the video from mediaItems.
    const { model: regimeModel } = useRegimeStore();

    return (
        <StyledGalleryMediaPreviewer
            items={filteredMediaItems}
            options={{ hideGalleryThumbnailCarousel: !!hasOneItem }}
            onOpenCallback={(pswpInstance) => onOpen(pswpInstance)}
            onThumbnailClick={({ index, type }) => onThumbnailClick({ index, type })}
            onPaginate={(index, type) => onPaginate({ index, type })}
            onCarouselScrollClick={(scrollType) => onCarouselScrollClick(scrollType)}
            $isChanel={isChanel}
        >
            {(splashes || emaLogo) && <SplashOverlay splashes={splashes} emaLogo={emaLogo} />}

            {regimeModel && (
                <WhenSplitTestVariant variant="Variant" systemSplitTestKey="PdpRegimesPlacement">
                    <RegimeQuickViewButton />
                </WhenSplitTestVariant>
            )}
        </StyledGalleryMediaPreviewer>
    );
}
